import { DATETIME_OPTIONS, DATE_OPTIONS } from "@/utils/const";
import parsePhoneNumberFromString from "libphonenumber-js";

const filters = {
  firstLetter(value: string) {
    return value != null ? value[0] : "";
  },

  capitalize(value: string) {
    return value != null ? value.charAt(0).toUpperCase() + value.slice(1) : "";
  },

  uppercase(value: string) {
    return value != null ? value.toString().toUpperCase() : "";
  },

  getDateOnly(value: string) {
    return value != null ? new Date(value).toLocaleDateString("en-US", DATE_OPTIONS) : "-";
  },

  getTimeOnly(value: string) {
    return value != null
      ? new Date(value).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";
  },

  getTimeOnly24Hrs(value: string) {
    return value != null
      ? new Date(value).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : "-";
  },

  getDateAndTime(value: string) {
    return value != null ? new Date(value).toLocaleDateString("en-US", DATETIME_OPTIONS) : "-";
  },

  checkDateRange(startDate: Date, endDate: Date): number {
    const now = new Date();
    if (now > endDate) {
      return 0; // Past
    } else if (now > startDate && now < endDate) {
      return 1; // Within the range
    } else if (now < startDate) {
      return 0;
    } else {
      return 0;
    }
  },

  formatDate(value: string) {
    return new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  },

  formatPrice(value: number, currencyISO3: string | null) {
    let currencyObj: Intl.NumberFormatOptions = {
      style: "currency",
      ...(currencyISO3 != null ? { currency: currencyISO3 } : {}),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };

    const formatter = new Intl.NumberFormat("en-US", currencyObj);
    return value != null ? formatter.format(value) : "-";
  },

  /**
   * Formats any phone string for display:
   * - Works with E.164 (+15106108669) and messy legacy values like "(408) 823-8454", "+506 8997 5423"
   * - Assumes Canada (CA) when no country code is present
   * - Returns the original input if it can’t be parsed
   */
  formatPhoneNumber(input: string): string {
    if (!input || typeof input !== "string") return input;

    const original = input.trim();
    // 1) First try: let the lib parse as-is (default country CA if no +)
    try {
      const p1 = parsePhoneNumberFromString(original, "CA");
      if (p1?.isValid()) return p1.formatInternational();
    } catch {}

    // 2) Light cleanup & another parse attempt
    const cleaned = this.cleanupNumber(original);
    try {
      const p2 = parsePhoneNumberFromString(cleaned, "CA");
      if (p2?.isValid()) return p2.formatInternational();
    } catch {}

    // 3) If still not valid, try a conservative NANP (+1) guess for 10/11-digit inputs
    const digits = cleaned.replace(/[^\d+]/g, "");
    if (!digits.startsWith("+")) {
      const only = digits.replace(/\D/g, "");
      if (only.length === 10) {
        const guess = "+1" + only;
        const p3 = parsePhoneNumberFromString(guess);
        if (p3?.isValid()) return p3.formatInternational();
      }
      if (only.length === 11 && only.startsWith("1")) {
        const guess = "+" + only;
        const p4 = parsePhoneNumberFromString(guess);
        if (p4?.isValid()) return p4.formatInternational();
      }
    }

    // 4) Give up gracefully
    return original;
  },

  // --- helpers ---

  /** Minimal cleanup without changing the meaning */
  cleanupNumber(s: string): string {
    // Normalize unicode & Arabic-Indic digits
    s = this.toAsciiDigits(s.normalize("NFKC")).trim();

    // Drop common trailing extensions: ext, x, # + digits (end only)
    s = s.replace(/\s*(?:ext\.?|x|#)\s*\d+\s*$/i, "");

    // Convert international prefixes "00" or "011" → "+"
    s = s.replace(/^\s*(?:00|011)\s*/, "+");

    // Keep a single leading '+' and digits; drop other punctuation/spaces
    const startsPlus = s.startsWith("+");
    s = (startsPlus ? "+" : "") + s.replace(/[^\d]/g, "");
    return s;
  },

  toAsciiDigits(s: string): string {
    return s.replace(/[\u0660-\u0669\u06F0-\u06F9]/g, (d) => {
      const code = d.charCodeAt(0);
      const zero = code >= 0x06f0 ? 0x06f0 : 0x0660;
      return String(code - zero);
    });
  },

  round(value: number) {
    return value.toFixed(2);
  },

  capitalizedFirstLetter(str: string) {
    if (!str) return "";
    return str.charAt(0).toUpperCase();
  },

  formatNumberWithComma(number: number) {
    if (isNaN(number)) {
      return "Invalid number";
    }
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  },

  normalizeWebsite(websiteUrl: string) {
    const url = websiteUrl || "";
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    return url;
  },
};

export default filters;
